import { useCallback } from "react";

import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Icon } from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import api from "api";

import CardHeader, { FeedCardHeaderProps } from "./CardHeader";

export const CardFeed = styled.div`
  width: 100%;
  font-family: "Inter", sans-serif;
  box-shadow: ${({ theme }) => theme.boxShadow};
  border: none;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  padding: 15px;
  .card-action {
    flex: 0.25;
    display: flex;
    align-self: flex-end;
    justify-content: center;
    align-items: center;
  }
  &:hover {
    background-color: #f0f0f0;
  }
  .hover-action {
    padding: 6px;
    &:hover {
      background-color: rgb(245, 245, 245) !important;
      border-radius: 15%;
    }
  }
  p {
    font-size: 1.2em;
    color: #4d4949;
    max-height: 100%;
  }
  .content-name {
    font-size: 1.2em;
    color: #000;
    margin-left: 15px;
    font-weight: 600;
    text-overflow: ellipsis;
    overflow-wrap: ellipsis;
    a {
      color: #5d5d5d;
    }
  }
  .content-meta-container {
    display: flex;
    flex-direction: column;
  }
  .content-meta {
    color: rgb(136, 132, 132);
    margin-left: 15px;
    font-weight: 600;
    text-overflow: ellipsis;
    overflow-wrap: ellipsis;
  }
  span {
    color: #4d4949;
  }
  .cover-and-text {
    display: flex;
    justify-content: flex-start;
    flex-direction: row;
    align-items: center;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  .cover-image {
    width: 3.2em !important;
    height: 3.2em !important;
  }
  .comment-column {
    height: 100%;
  }
  .comment-column-container {
    display: flex;
    height: 100%;
  }
  .comment-column-container p {
    font-size: 1em;
    line-height: 1.2em;
    white-space: pre-line;
    color: #4d4949;
    overflow: hidden;
    word-break: break-word;
  }
  .image-link {
    a {
      width: 60px !important;
      height: 50px !important;
    }
  }
`;

export const ContentArea = styled.div`
  width: 100%;
  flex-direction: column;
  display: flex;
  gap: 12px;
  margin-top: 14px;
`;

type FeedCardAction = {
  label: string;
  to: string;
  icon?: string;
  onClick?: () => void;
};

type FeedCardProps = {
  cardContent: React.ReactNode;
  toLink: string;
  ideaId?: string;
  commentId?: string;
  setIdeaPreviewId?: (ideaId: string) => void;
  extraActions?: FeedCardAction[];
  footerExtra?: React.ReactNode;
} & FeedCardHeaderProps;

const FeedCard = ({
  cardContent,
  toLink,
  ideaId,
  commentId,
  setIdeaPreviewId,
  extraActions = [],
  footerExtra,
  ...rest
}: FeedCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleClickFeedItem = useCallback(() => {
    api.journey.record("feed", toLink || ideaId || null, "clickedFeedCard");
    if (ideaId) {
      setIdeaPreviewId?.(ideaId);
      if (commentId) {
        const params = new URLSearchParams(location.search);
        params.set("comment", commentId);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else navigate(toLink);
  }, [commentId, ideaId, location, navigate, setIdeaPreviewId, toLink]);

  const handleActionClick = useCallback(
    (event: React.MouseEvent, action?: FeedCardAction) => {
      event.stopPropagation();
      api.journey.record(
        "feed",
        action?.to || toLink || ideaId || null,
        "clickedFeedCard",
      );
      if (action?.onClick) {
        action.onClick();
        return;
      }
      if (action?.to) {
        navigate(action.to);
        return;
      }
      handleClickFeedItem();
    },
    [handleClickFeedItem, ideaId, navigate, toLink],
  );

  if (!cardContent) {
    return null;
  }
  return (
    <CardFeed
      style={{ marginBottom: theme.sizes.isMobile ? 10 : 20, marginTop: 3 }}
    >
      <CardHeader {...rest} />
      <ContentArea
        onClick={() => {
          handleClickFeedItem();
        }}
      >
        {cardContent}
        <div
          style={{
            display: "flex",
            alignSelf: "flex-end",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {footerExtra ?
            <div
              onClick={(event) => event.stopPropagation()}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              {footerExtra}
            </div>
          : null}
          {extraActions.map((action) => (
            <Link
              key={action.label}
              className="hover-action"
              to={action.to}
              onClick={(event) => handleActionClick(event, action)}
              style={{ fontWeight: "bold", minWidth: "max-content" }}
            >
              {action.label.toUpperCase()}{" "}
              {action.icon ?
                <Icon name={action.icon as "comment outline"} />
              : null}
            </Link>
          ))}
          <Link
            className="hover-action"
            to={ideaId ? null : toLink}
            onClick={(event) => handleActionClick(event)}
            style={{ fontWeight: "bold", minWidth: "max-content" }}
          >
            {t("generic.viewType", { entity: "" }).toUpperCase()}{" "}
            <Icon name="arrow alternate circle right outline" />
          </Link>
        </div>
      </ContentArea>
    </CardFeed>
  );
};

export default FeedCard;
